import type { Theme } from '@withjoy/joykit';
import { css, Flex, styled } from '@withjoy/joykit';
import { animationTransition } from '@shared/utils/animationTransition';

const hideOnLoading = () => css`
  visibility: hidden;
  opacity: 0;
`;

const showOnLoading = () => css`
  visibility: initial;
  transition: ${animationTransition('opacity')};
`;

export const StyledFlexAloha = styled(Flex)<{ loading: boolean; isPreviewing: boolean; heightOffset?: number }>`
  width: 100%;
  position: relative;
  flex-direction: column;
  ${({ loading }) => (loading ? hideOnLoading : showOnLoading)}

  height: ${({ heightOffset }) => (heightOffset ? `calc(100vh - ${heightOffset}px)` : '100vh')};
  max-height: 100%;
  z-index: 1;
  overflow: auto;

  ${({ isPreviewing }) =>
    isPreviewing &&
    `
    * {
        pointer-events: none;
      }
  `}
`;

const disableTopMarginOnMobile = (theme: Theme) => `
  @media (max-width: ${theme.breakpoints.sm4}) {
    & > :first-child {
      margin-top: 0;
    }
  }
`;

// The deep-linked section fills the viewport so the section after it starts below the
// fold. Its widgets render client-side and arrive seconds later (measured 586px → 1847px
// on mobile), and without this floor that growth displaces the next section, which was
// visible underneath. A permanent floor, not a loading placeholder: it holds after the
// content swap too, so a short section can't let the next one drift back into view.
// PRNT-4799.
// The target's floor, plus anchor confinement: the deep-link target is the only section
// eligible for scroll anchoring (see scrollAnchorCandidacy), but its own skeleton→content swap
// removes whatever node Chrome anchored on — Chrome then re-anchors deeper in the new content
// and tracks THAT, sliding the viewport ~a full section into the target (the deterministic 0.97
// excursion). Excluding the target's content leaves only the never-mutating 1px sentinel
// rendered as its first child, so anchoring can only ever mean "keep the target's top pinned".
// PRNT-4799.
const fillViewport = ({ $fillViewport }: { $fillViewport?: boolean }) =>
  $fillViewport
    ? `min-height: 100vh;
  & > *:not([data-scroll-anchor-sentinel]) {
    overflow-anchor: none;
  }`
    : '';

// Scroll anchoring adjusts the scroll offset pre-paint (CLS-exempt) as content changes size, but
// the browser picks the anchor node itself. On a deep link the only content-stable elements early
// on are the sections BELOW the target (it and everything above are still loading), so Chrome
// anchored on those — and when the target's own content arrived, it kept the section below pinned
// and slid the viewport past the target; the later anchor re-selection painted as a 0.97 shift.
// Excluding every non-target section (and the footer, at its use site) from candidacy — `none`
// prunes the element AND its subtree — leaves the target as the only legal anchor. The JS scroll
// keepers stay as the cross-browser guarantee; their delta math already absorbs anchoring's
// adjustments. PRNT-4799.
const scrollAnchorCandidacy = ({ $fillViewport }: { $fillViewport?: boolean }) => ($fillViewport ? '' : 'overflow-anchor: none;');

// The footer's half of the exclusion above. Real CSS, not `__css`: `overflow-anchor` isn't in
// joykit's style-prop map so a `__css={{ overflowAnchor }}` is silently dropped (and fails
// typecheck). PRNT-4799.
export const FooterAnchorExcludedFlex = styled(Flex)`
  overflow-anchor: none;
`;

export const PageContainer = styled.div<{
  backgroundColor?: string;
  textColor?: string;
  disableBottomMargin?: boolean;
  disableMobileTopMargin?: boolean;
  $fillViewport?: boolean;
}>`
  display: inline-block;
  width: 100%;
  background-color: ${({ backgroundColor }) => backgroundColor};
  color: ${({ textColor }) => textColor};
  ${fillViewport}
  ${scrollAnchorCandidacy}

  ${({ disableBottomMargin, theme }) => {
    if (disableBottomMargin) {
      return;
    }

    return `
      @media (min-width: ${theme.breakpoints.sm2}) {
        & > :first-child {
          margin-bottom: 4rem;
        }
      }
  `;
  }}

  ${({ disableMobileTopMargin, theme }) => {
    if (!disableMobileTopMargin) {
      return;
    }
    return disableTopMarginOnMobile(theme);
  }}
`;

export const CustomPageContainer = styled.div<{ backgroundColor?: string; textColor?: string; disableMobileTopMargin?: boolean; $fillViewport?: boolean }>`
  display: flex;
  width: 100%;
  background-color: ${({ backgroundColor }) => backgroundColor};
  color: ${({ textColor }) => textColor};
  ${fillViewport}
  ${scrollAnchorCandidacy}

  justify-content: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm2}) {
    margin-bottom: 4rem;
  }

  ${({ disableMobileTopMargin, theme }) => {
    if (!disableMobileTopMargin) {
      return;
    }
    return disableTopMarginOnMobile(theme);
  }}
`;
